import { useEffect } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { useSaveData } from "../../../../hooks/useSaveData";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { ScheduleEmailForm } from "./schedule-email-form.component";
import { useReactHookForm } from "../../../../hooks/useReactHookForm";
import { scheduleEmailFormSchema } from "./schedule-email-form.schema";
import { queryBuilderApiKeyStore } from "../../../../api/query-builder-api-key-store";
import { LoadingIndicator } from "../../../../components/loading-indicator.component";
import { ReactHookForm } from "../../../../components/hook-form/react-hook-form.component";
import dayjs from "dayjs";
import { useAuthorize } from "../../../../hooks/use-authorize";
import { UserPermissionName } from "../../../../models/user-permission-name";


interface ScheduleEmailDialog {
    reportID: any;
    onClosed: () => void;
}

export const ScheduleEmailDialog = ({ reportID, onClosed }: ScheduleEmailDialog) => {

    const form = useReactHookForm({
        defaultValues: {
            reportID: reportID,
            scheduleID: 0,
            scheduleInterval: '',
            scheduleDate: '',
            scheduleDay: '',
            scheduleTime: '',
        },
        yupSchema: scheduleEmailFormSchema
    });

    const { data: scheduleQueryData, isLoading: isScheduleQueryLoading } = useLoadData<ApiResponse<any>>(queryBuilderApiKeyStore.getScheduleQuery(reportID));

    const { isLoading, mutateAsync } = useSaveData<any>(queryBuilderApiKeyStore.scheduleQuery());

    useEffect(() => {
        if (scheduleQueryData) {
            form.reset({
                reportID: reportID,
                scheduleID: scheduleQueryData?.data?.scheduleID,
                scheduleInterval: scheduleQueryData?.data?.interval,
                scheduleDate: scheduleQueryData?.data?.date ?? '',
                scheduleDay: scheduleQueryData?.data?.day ?? '',
                scheduleTime: scheduleQueryData?.data?.time ?? '',
            })
        }
    }, [scheduleQueryData])

    const submitHanlder = async (data: any) => {
        const scheduleData = {
            reportID: data?.reportID ?? 0,
            scheduleID: data?.scheduleID ?? 0,
            interval: data?.scheduleInterval ?? null,
            date: data?.scheduleDate ? dayjs(data?.scheduleDate).format('MM/DD/YYYY') : null,
            day: data?.scheduleDay ?? null,
            time: data?.scheduleTime ?? null,
        }
        await mutateAsync({ data: scheduleData });
        onClosed();
    };

    const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

    return (
        <Dialog open={true} fullWidth={true} maxWidth='sm'>
            <DialogTitle id="form-dialog-title">Schedule Report Email</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClosed}
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Tooltip title={<Typography variant="body1">Close</Typography>}>
                    <CloseIcon />
                </Tooltip>
            </IconButton>
            <ReactHookForm formName="SEVE_FORM" onSubmit={submitHanlder} formContextProps={form}>
                <DialogContent dividers className="popupDilog">
                    <LoadingIndicator isLoading={isScheduleQueryLoading} isSpinner />
                    <ScheduleEmailForm />
                </DialogContent>
                <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
                    <DialogActions className="spaceBetween">
                        <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={isLoading || isReadOnlyPermission}
                            variant="contained"
                            style={{ width: '130px' }}
                            onClick={() => form.handleSubmit(submitHanlder)()}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </div>
            </ReactHookForm>
        </Dialog>
    );
}